import { useEffect, useMemo, useRef, useState } from "react";
import { CopyCode } from "@/components/CopyCode";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import {
  Lock, LockOpen, Trash2, Plus, ArrowLeft, CheckCircle2, XCircle,
  Smartphone, ArrowRight, Copy, Info, Delete, ShoppingCart, Sparkles, Bluetooth, MessageCircle, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatBs, formatUsd, formatTime } from "@/lib/format";
import { isWebBluetoothAvailable, printTicketViaBluetooth, getSavedPrinter } from "@/lib/bluetoothPrint";
import { buildWhatsappTicketUrl } from "@/lib/whatsappShare";

const ZODIAC_SIGNS = [
  "Aries", "Tauro", "Géminis", "Cáncer", "Leo", "Virgo",
  "Libra", "Escorpio", "Sagitario", "Capricornio", "Acuario", "Piscis",
];
const CARACAS_OFFSET_MS = -4 * 60 * 60 * 1000;
const caracasDateStr = (d) => new Date(d.getTime() + CARACAS_OFFSET_MS).toISOString().slice(0, 10);
const isTodayCaracas = (iso) => (iso ? caracasDateStr(new Date(iso)) === caracasDateStr(new Date()) : false);

/**
 * Modo Kiosco (autogestión) — pantalla completa touch-first.
 * Flujo:
 *   1) PIN
 *   2) Selección de lotería/horario + número + monto (agrega al carrito)
 *   3) Instrucciones de pago móvil + referencia
 *   4) Ticket con QR (el cliente lo lleva a caja para conciliar)
 * Reinicio automático por inactividad (60s en el paso final).
 */
export default function KioscoPage() {
  const { taquillaId } = useParams();
  const [step, setStep] = useState("loading");
  const [status, setStatus] = useState(null);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [session, setSession] = useState(null);       // {token, taquilla, banca}
  const [lotteries, setLotteries] = useState([]);
  const [sorteos, setSorteos] = useState([]);
  const [selectedLotteryId, setSelectedLotteryId] = useState(null);
  const [selectedSorteoId, setSelectedSorteoId] = useState(null);
  const [selectedSign, setSelectedSign] = useState("");
  const [numberInput, setNumberInput] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [plays, setPlays] = useState([]);
  const [rate, setRate] = useState(0);
  const [customerName, setCustomerName] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("pago_movil"); // pago_movil | debito_inmediato
  const [ticket, setTicket] = useState(null);
  const [emitting, setEmitting] = useState(false);
  const [processing, setProcessing] = useState(null); // { intent_id, elapsed }
  const [pinLockUntil, setPinLockUntil] = useState(0); // epoch ms
  const [pinLockRemaining, setPinLockRemaining] = useState(0);
  const [captcha, setCaptcha] = useState(null); // { challenge_id, question } | null
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [sessionBlocked, setSessionBlocked] = useState(false);
  const idleTimer = useRef(null);
  const pollTimer = useRef(null);
  const pinLockTimer = useRef(null);

  const lotteryMap = useMemo(() => Object.fromEntries(lotteries.map((l) => [l.id, l])), [lotteries]);
  const sorteoMap  = useMemo(() => Object.fromEntries(sorteos.map((s) => [s.id, s])),  [sorteos]);
  const rateVal = rate || 40;
  const totalBs = useMemo(() => plays.reduce((a, p) => a + p.amount_bs, 0), [plays]);
  const totalUsd = useMemo(() => (rateVal > 0 ? totalBs / rateVal : 0), [totalBs, rateVal]);
  const selectedLottery = lotteryMap[selectedLotteryId];
  const selectedSorteo = sorteoMap[selectedSorteoId];
  const numberLen = selectedLottery?.number_length || 3;
  const needsSign = !!selectedLottery?.has_zodiac_sign;
  const groupSorteos = useMemo(
    () => (selectedLotteryId ? sorteos.filter((s) => s.lottery_type_id === selectedLotteryId) : []),
    [selectedLotteryId, sorteos]
  );

  // Fullscreen
  const goFullscreen = async () => {
    try { if (!document.fullscreenElement) await document.documentElement.requestFullscreen(); } catch { /* ignore */ }
  };

  // Reset idle timer en el paso "ticket" (60s → volver al inicio)
  const resetIdle = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      setTicket(null);
      setPlays([]);
      setCustomerName("");
      setPaymentMethod("pago_movil");
      setNumberInput("");
      setAmountInput("");
      setSelectedSign("");
      setStep("sell");
    }, 60000);
  };
  useEffect(() => () => idleTimer.current && clearTimeout(idleTimer.current), []);
  useEffect(() => {
    if (step === "ticket") resetIdle();
    return () => idleTimer.current && clearTimeout(idleTimer.current);
  }, [step]);

  // Cargar estado inicial (público)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/kiosco/${taquillaId}/status`);
        setStatus(r.data);
        if (!r.data.kiosco_enabled) {
          setStep("disabled");
          return;
        }
        setStep("pin");
      } catch {
        setStep("notfound");
      }
    })();
  }, [taquillaId]);

  // Al autenticarse, cargar loterías/sorteos/tasa vía endpoint kiosco
  const loadCatalog = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const r = await axios.get(`${API_URL}/kiosco/${taquillaId}/catalog`, { headers });
      const rL = r.data.lotteries || [];
      const rS = (r.data.sorteos || []).filter((s) => isTodayCaracas(s.scheduled_at));
      setLotteries(rL);
      setSorteos(rS);
      setRate(r.data.rate?.bs_per_usd || 40);
      if (rL[0]?.id) setSelectedLotteryId(rL[0].id);
    } catch (e) {
      toast.error("No se pudo cargar el catálogo del kiosco");
    }
  };

  const doAuth = async () => {
    setPinError("");
    if (pinLockRemaining > 0) return;
    if (!/^\d{4,8}$/.test(pin)) {
      setPinError("El PIN debe tener 4 a 8 dígitos");
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/kiosco/${taquillaId}/auth`, { pin });
      setSession(r.data);
      await loadCatalog(r.data.token);
      setStep("sell");
      goFullscreen();
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (st === 429) {
        const retry = Number(detail?.retry_after || e?.response?.headers?.["retry-after"] || 300);
        setPinLockUntil(Date.now() + retry * 1000);
        setPinLockRemaining(retry);
        setPinError(typeof detail === "object" ? detail.message : "Demasiados intentos. Espera unos minutos.");
      } else {
        setPinError(typeof detail === "string" ? detail : (detail?.message || "PIN incorrecto"));
      }
      setPin("");
    }
  };

  // Timer countdown para desbloqueo del PIN
  useEffect(() => {
    if (pinLockUntil <= Date.now()) return;
    if (pinLockTimer.current) clearInterval(pinLockTimer.current);
    pinLockTimer.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((pinLockUntil - Date.now()) / 1000));
      setPinLockRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(pinLockTimer.current);
        setPinError("");
      }
    }, 1000);
    return () => pinLockTimer.current && clearInterval(pinLockTimer.current);
  }, [pinLockUntil]);

  const addPlay = () => {
    if (!selectedSorteoId) return toast.error("Selecciona un horario");
    if (!numberInput) return toast.error("Ingresa un número");
    const amt = parseFloat(amountInput);
    if (!amt || amt <= 0) return toast.error("Ingresa un monto");
    if (needsSign && !selectedSign) return toast.error("Selecciona un signo zodiacal");
    const lot = lotteryMap[selectedLottery.id];
    const len = lot?.number_length || 3;
    const norm = numberInput.padStart(len, "0").slice(-len);
    const play = {
      lottery_type_id: lot.id,
      sorteo_id: selectedSorteoId,
      number: norm,
      sign: needsSign ? selectedSign : null,
      amount_bs: amt,
      amount_usd: Math.round((amt / rateVal) * 100) / 100,
    };
    setPlays((prev) => [...prev, play]);
    setNumberInput("");
    setAmountInput("");
    setSelectedSign("");
    toast.success("Jugada agregada");
  };

  const emitTicket = async (captchaOverride) => {
    if (!session?.token) return;
    if (plays.length === 0) return toast.error("Agrega al menos una jugada");
    setEmitting(true);
    try {
      const headers = { Authorization: `Bearer ${session.token}` };
      const body = {
        plays: plays.map((p) => ({ sorteo_id: p.sorteo_id, number: p.number, amount_bs: p.amount_bs, sign: p.sign || null })),
        method: paymentMethod,
      };
      if (captchaOverride?.challenge_id && captchaOverride?.answer) {
        body.captcha_id = captchaOverride.challenge_id;
        body.captcha_answer = captchaOverride.answer;
      }
      const r = await axios.post(`${API_URL}/kiosco/${taquillaId}/payment/init`, body, { headers });
      const intent = r.data.payment_intent_id;
      setCaptcha(null);
      setCaptchaAnswer("");
      setStep("processing");
      setProcessing({ intent_id: intent, elapsed: 0 });
      startPolling(intent);
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (st === 428 && detail?.captcha_required) {
        // Muestra el captcha suave y detiene el emit
        setCaptcha({ challenge_id: detail.challenge_id, question: detail.question });
        setCaptchaAnswer("");
        toast.message("Verifica que no eres un robot.");
      } else if (st === 429 && detail?.reason === "session_limit") {
        setSessionBlocked(true);
        toast.error("Límite de compras alcanzado. Cierra sesión y vuelve a ingresar el PIN.");
      } else if (e?.response?.status === 409 && detail?.agotado) {
        toast.error("Algunas jugadas están agotadas. Elimínalas y reintenta.");
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "No se pudo iniciar el pago");
        toast.error(msg);
      }
    } finally {
      setEmitting(false);
    }
  };

  const submitCaptcha = async () => {
    if (!captcha?.challenge_id) return;
    const ans = captchaAnswer.trim();
    if (!ans) return toast.error("Ingresa la respuesta");
    await emitTicket({ challenge_id: captcha.challenge_id, answer: ans });
  };

  const logoutKiosco = () => {
    stopPolling();
    setSession(null);
    setSessionBlocked(false);
    setCaptcha(null);
    setCaptchaAnswer("");
    setPlays([]);
    setTicket(null);
    setProcessing(null);
    setPin("");
    setPinError("");
    setStep("pin");
  };

  const stopPolling = () => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  };

  const startPolling = (intentId) => {
    stopPolling();
    const headers = { Authorization: `Bearer ${session.token}` };
    let elapsed = 0;
    pollTimer.current = setInterval(async () => {
      elapsed += 2;
      setProcessing((p) => (p ? { ...p, elapsed } : p));
      try {
        const r = await axios.get(`${API_URL}/kiosco/${taquillaId}/payment/${intentId}`, { headers });
        const st = r.data.status;
        if (st === "active" && r.data.ticket) {
          stopPolling();
          setTicket(r.data.ticket);
          setProcessing(null);
          setStep("ticket");
        } else if (st === "void") {
          stopPolling();
          setProcessing(null);
          toast.error("El pago fue rechazado. Intenta nuevamente.");
          setStep("payment");
        }
        // else: sigue pending_payment
      } catch {
        // silent — reintenta en próximo tick
      }
      if (elapsed >= 120) {
        // Timeout de 2 minutos
        stopPolling();
        setProcessing(null);
        toast.error("Tiempo agotado. Vuelve a intentar el pago.");
        setStep("payment");
      }
    }, 2000);
  };

  useEffect(() => () => stopPolling(), []);

  const restart = () => {
    stopPolling();
    setTicket(null);
    setPlays([]);
    setNumberInput("");
    setAmountInput("");
    setSelectedSign("");
    setCustomerName("");
    setPaymentMethod("pago_movil");
    setProcessing(null);
    setStep("sell");
  };

  // -------- Renderers --------
  if (step === "loading") {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center">
        <div className="text-muted-foreground text-sm">Cargando…</div>
      </div>
    );
  }

  if (step === "notfound") {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center p-6 text-center">
        <div>
          <XCircle size={48} className="mx-auto text-rose-400" />
          <h1 className="mt-4 text-2xl font-bold">Taquilla no encontrada</h1>
          <p className="mt-2 text-muted-foreground">Verifica el enlace del kiosco con el administrador.</p>
        </div>
      </div>
    );
  }

  if (step === "disabled") {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center p-6 text-center">
        <div>
          <Info size={48} className="mx-auto text-amber-400" />
          <h1 className="mt-4 text-2xl font-bold">Kiosco no habilitado</h1>
          <p className="mt-2 text-muted-foreground max-w-md">
            Esta taquilla ({status?.name}) no tiene el modo de autogestión activo. Pide al administrador
            configurar un PIN y habilitar el kiosco.
          </p>
        </div>
      </div>
    );
  }

  if (step === "pin") {
    return (
      <PinScreen
        pin={pin} setPin={setPin} pinError={pinError} doAuth={doAuth}
        taquillaName={status?.name}
        lockRemaining={pinLockRemaining}
      />
    );
  }

  if (step === "processing") {
    return <ProcessingScreen elapsed={processing?.elapsed || 0} onCancel={() => { stopPolling(); setProcessing(null); setStep("payment"); }} totalBs={totalBs} />;
  }

  if (step === "ticket" && ticket) {
    return (
      <>
        <TicketScreen ticket={ticket} banca={session?.banca} taquilla={session?.taquilla} onRestart={restart} rate={rate} sorteoMap={sorteoMap} lotteryMap={lotteryMap} />
        <CaptchaDialog
          captcha={captcha} answer={captchaAnswer} setAnswer={setCaptchaAnswer}
          onSubmit={submitCaptcha} onClose={() => setCaptcha(null)} loading={emitting}
        />
      </>
    );
  }

  if (step === "payment") {
    return (
      <>
        <PaymentScreen
          plays={plays}
          totalBs={totalBs}
          totalUsd={totalUsd}
          rate={rate}
          banca={session?.banca}
          taquilla={session?.taquilla}
          customerName={customerName}
          setCustomerName={setCustomerName}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          onBack={() => setStep("sell")}
          onEmit={() => emitTicket()}
          emitting={emitting}
          sessionBlocked={sessionBlocked}
          onLogout={logoutKiosco}
        />
        <CaptchaDialog
          captcha={captcha} answer={captchaAnswer} setAnswer={setCaptchaAnswer}
          onSubmit={submitCaptcha} onClose={() => setCaptcha(null)} loading={emitting}
        />
      </>
    );
  }

  // step === "sell"
  return (
    <SellScreen
      taquilla={session?.taquilla}
      lotteries={lotteries}
      lotteryMap={lotteryMap}
      sorteoMap={sorteoMap}
      groupSorteos={groupSorteos}
      selectedLotteryId={selectedLotteryId}
      setSelectedLotteryId={setSelectedLotteryId}
      selectedSorteoId={selectedSorteoId}
      setSelectedSorteoId={setSelectedSorteoId}
      numberInput={numberInput}
      setNumberInput={setNumberInput}
      amountInput={amountInput}
      setAmountInput={setAmountInput}
      numberLen={numberLen}
      needsSign={needsSign}
      selectedSign={selectedSign}
      setSelectedSign={setSelectedSign}
      plays={plays}
      setPlays={setPlays}
      totalBs={totalBs}
      totalUsd={totalUsd}
      rateVal={rateVal}
      addPlay={addPlay}
      onContinue={() => {
        if (plays.length === 0) return toast.error("Agrega al menos una jugada");
        setStep("payment");
      }}
    />
  );
}

// ============================================================
// Subpantallas
// ============================================================

function PinScreen({ pin, setPin, pinError, doAuth, taquillaName, lockRemaining = 0 }) {
  const locked = lockRemaining > 0;
  const digits = ["1","2","3","4","5","6","7","8","9","","0","del"];
  const mm = String(Math.floor(lockRemaining / 60)).padStart(2, "0");
  const ss = String(lockRemaining % 60).padStart(2, "0");
  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center p-4 select-none" data-testid="kiosco-pin-screen">
      <div className="w-full max-w-sm">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-primary/20 border-2 border-primary/60 grid place-items-center">
            <Lock size={28} className="text-primary" />
          </div>
          <h1 className="mt-4 text-2xl font-bold">Kiosco de autogestión</h1>
          <div className="text-sm font-semibold text-muted-foreground mt-1">{taquillaName || "Taquilla"}</div>
          <p className="mt-2 text-sm text-muted-foreground">Ingresa el PIN de kiosco para comenzar.</p>
        </div>
        <Card className="mt-6 bg-card border-2 border-border p-5">
          {locked && (
            <div className="mb-4 rounded-xl border-2 border-rose-500/40 bg-rose-500/10 p-3 text-center" data-testid="kiosco-pin-lock">
              <ShieldAlert size={22} className="mx-auto text-rose-300"/>
              <div className="mt-1 text-sm font-bold text-rose-100">Demasiados intentos</div>
              <div className="text-xs font-semibold text-rose-200/80">Reintenta en <span className="font-mono tabular-nums" data-testid="kiosco-pin-lock-countdown">{mm}:{ss}</span></div>
            </div>
          )}
          <div className="flex justify-center gap-2 mb-4" data-testid="kiosco-pin-display">
            {[0,1,2,3,4,5,6,7].slice(0, Math.max(4, pin.length)).map((i) => (
              <div key={i} className={`h-4 w-4 rounded-full border-2 ${i < pin.length ? "bg-primary border-primary" : "border-border"}`}/>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {digits.map((d, i) => (
              <button
                key={i}
                type="button"
                disabled={!d || locked}
                onClick={() => {
                  if (d === "del") setPin((p) => p.slice(0, -1));
                  else if (d && pin.length < 8) setPin((p) => p + d);
                }}
                className={`h-14 rounded-xl border-2 text-xl font-bold ${d ? "border-border hover:bg-secondary/70 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed" : "invisible"}`}
                data-testid={`kiosco-pin-key-${d || "empty"}`}
              >
                {d === "del" ? <Delete size={22} className="mx-auto"/> : d}
              </button>
            ))}
          </div>
          {pinError && !locked && <div className="mt-3 text-sm text-rose-400 text-center font-semibold" data-testid="kiosco-pin-error">{pinError}</div>}
          <Button onClick={doAuth} className="w-full mt-4 h-12 text-base" disabled={pin.length < 4 || locked} data-testid="kiosco-pin-submit">
            <LockOpen size={18} className="mr-2"/> {locked ? "Bloqueado" : "Continuar"}
          </Button>
        </Card>
        <div className="text-center text-[11px] font-semibold text-muted-foreground mt-3">CONNECT · Sistema de Juegos</div>
      </div>
    </div>
  );
}

function SellScreen(props) {
  const {
    taquilla, lotteries, lotteryMap, sorteoMap, groupSorteos,
    selectedLotteryId, setSelectedLotteryId, selectedSorteoId, setSelectedSorteoId,
    numberInput, setNumberInput, amountInput, setAmountInput, numberLen,
    needsSign, selectedSign, setSelectedSign,
    plays, setPlays, totalBs, totalUsd, rateVal, addPlay, onContinue,
  } = props;
  const [activeField, setActiveField] = useState("number");
  const [amountPreset] = useState([10, 20, 50, 100, 200, 500]);
  const press = (k) => {
    if (activeField === "number") {
      if (numberInput.length >= numberLen) return;
      setNumberInput(numberInput + k);
    } else setAmountInput(amountInput + k);
  };
  const back = () => activeField === "number" ? setNumberInput(numberInput.slice(0, -1)) : setAmountInput(amountInput.slice(0, -1));

  return (
    <div className="min-h-screen bg-background text-foreground p-4 pb-24" data-testid="kiosco-sell-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Kiosco</div>
            <div className="text-lg font-bold">{taquilla?.name}</div>
          </div>
          <Badge className="bg-primary/15 text-primary border-2 border-primary/40 font-bold">
            <ShoppingCart size={14} className="mr-1"/> {plays.length} jugada{plays.length !== 1 ? "s" : ""}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* Loterías */}
          <Card className="bg-card border-2 border-border p-3 lg:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Elige tu lotería</div>
            <div className="grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto">
              {lotteries.map((l) => {
                const active = selectedLotteryId === l.id;
                return (
                  <button
                    key={l.id}
                    onClick={() => { setSelectedLotteryId(l.id); setSelectedSorteoId(null); }}
                    className={`rounded-xl border-2 p-3 text-left transition-colors active:scale-95 ${active ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/60"}`}
                    style={active ? { boxShadow: `inset 4px 0 0 ${l.color || "#1FB6A6"}` } : {}}
                    data-testid={`kiosco-lottery-${l.code}`}
                  >
                    <div className="text-sm font-bold leading-tight">{l.name}</div>
                    <div className="text-[10px] font-semibold text-muted-foreground mt-0.5">Paga {l.payout_multiplier}x · {l.number_length}d</div>
                  </button>
                );
              })}
            </div>
            {groupSorteos.length > 0 && (
              <>
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mt-3 mb-2">Horario</div>
                <div className="grid grid-cols-3 gap-1.5 max-h-[220px] overflow-y-auto">
                  {groupSorteos.map((s) => {
                    const active = selectedSorteoId === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSelectedSorteoId(s.id)}
                        className={`rounded-lg border-2 px-1 py-2 text-center text-xs transition-colors active:scale-95 ${active ? "border-primary bg-primary/15" : "border-border hover:bg-secondary/60"}`}
                        data-testid={`kiosco-sorteo-${s.id}`}
                      >
                        <div className="font-bold tabular-nums text-[13px]">{formatTime(s.scheduled_at)}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </Card>

          {/* Número + monto */}
          <Card className="bg-card border-2 border-border p-3 lg:col-span-4">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button type="button" onClick={() => setActiveField("number")} className={`rounded-xl border-2 p-3 text-left transition ${activeField === "number" ? "border-primary bg-primary/10 ring-2 ring-primary/50" : "border-border"}`} data-testid="kiosco-field-number">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Número · {numberLen}d</div>
                <div className="font-mono text-3xl tabular-nums font-bold min-h-[40px]" data-testid="kiosco-input-number">{numberInput || "—"}</div>
              </button>
              <button type="button" onClick={() => setActiveField("amount")} className={`rounded-xl border-2 p-3 text-left transition ${activeField === "amount" ? "border-primary bg-primary/10 ring-2 ring-primary/50" : "border-border"}`} data-testid="kiosco-field-amount">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Monto Bs</div>
                <div className="font-mono text-3xl tabular-nums font-bold min-h-[40px]" data-testid="kiosco-input-amount">{amountInput || "—"}</div>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-2">
              {["1","2","3","4","5","6","7","8","9",".","0","del"].map((k) => (
                <button
                  key={k}
                  onClick={() => k === "del" ? back() : k === "." ? (activeField === "amount" && !amountInput.includes(".") && setAmountInput((v) => (v || "0") + ".")) : press(k)}
                  className="h-14 rounded-xl border-2 border-border text-2xl font-bold hover:bg-secondary/70 active:scale-95 transition"
                  data-testid={`kiosco-key-${k}`}
                >
                  {k === "del" ? <Delete className="mx-auto" size={22}/> : k}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {amountPreset.map((v) => (
                <button
                  key={v}
                  onClick={() => { setActiveField("amount"); setAmountInput(String(v)); }}
                  className="rounded-md border-2 border-border py-1.5 text-xs font-bold hover:bg-secondary/70 active:scale-95 transition"
                  data-testid={`kiosco-amount-preset-${v}`}
                >
                  Bs {v}
                </button>
              ))}
            </div>

            {needsSign && (
              <div className="mb-2">
                <div className="text-[11px] font-bold uppercase tracking-widest text-violet-300 mb-1 flex items-center gap-1"><Sparkles size={12}/> Signo zodiacal</div>
                <div className="grid grid-cols-4 gap-1">
                  {ZODIAC_SIGNS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelectedSign(s)}
                      className={`rounded-md border-2 py-1 text-[11px] font-semibold transition active:scale-95 ${selectedSign === s ? "border-violet-400 bg-violet-500/20 text-violet-100" : "border-border hover:bg-secondary/60"}`}
                      data-testid={`kiosco-sign-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={addPlay} size="lg" className="w-full h-12 text-base" data-testid="kiosco-add-play">
              <Plus size={18} className="mr-2"/> Agregar jugada
            </Button>
          </Card>

          {/* Carrito */}
          <Card className="bg-card border-2 border-border p-3 lg:col-span-4 flex flex-col">
            <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Tu carrito</div>
            <div className="flex-1 overflow-y-auto space-y-1.5 max-h-[400px]" data-testid="kiosco-cart">
              {plays.length === 0 && (
                <div className="text-center text-sm font-semibold text-muted-foreground border-2 border-dashed border-border rounded-xl py-8">
                  Sin jugadas todavía
                </div>
              )}
              {plays.map((p, i) => {
                const lot = lotteryMap[p.lottery_type_id];
                const sor = sorteoMap[p.sorteo_id];
                return (
                  <div key={i} className="flex items-center justify-between rounded-lg border-2 border-border bg-secondary/40 px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-11 grid place-items-center rounded-md font-mono text-base font-bold border-2" style={{ backgroundColor: (lot?.color || "#1FB6A6") + "22", color: lot?.color || "#1FB6A6", borderColor: (lot?.color || "#1FB6A6") + "66" }}>{p.number}</div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold truncate">{lot?.name}{p.sign ? ` · ${p.sign}` : ""}</div>
                        <div className="text-[10px] font-semibold text-muted-foreground">{sor ? formatTime(sor.scheduled_at) : "—"}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="text-right">
                        <div className="font-mono tabular-nums text-xs font-bold">Bs {formatBs(p.amount_bs)}</div>
                      </div>
                      <button onClick={() => setPlays(plays.filter((_, j) => j !== i))} className="p-1 text-rose-300 hover:bg-rose-500/10 rounded-md" data-testid={`kiosco-remove-play-${i}`}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t-2 border-border mt-2 pt-2 space-y-1">
              <div className="flex justify-between text-sm font-bold">
                <span>Total Bs</span>
                <span className="tabular-nums text-primary text-lg" data-testid="kiosco-total-bs">{formatBs(totalBs)}</span>
              </div>
              <div className="flex justify-between text-xs font-semibold text-muted-foreground">
                <span>Total USD</span>
                <span className="tabular-nums">$ {formatUsd(totalUsd)}</span>
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>Tasa BCV</span>
                <span className="tabular-nums">Bs {formatBs(rateVal)}</span>
              </div>
            </div>
            <Button
              onClick={onContinue}
              disabled={plays.length === 0}
              className="w-full h-14 mt-3 text-base"
              data-testid="kiosco-continue-payment"
            >
              Ir a pagar <ArrowRight size={18} className="ml-2"/>
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PaymentScreen(props) {
  const {
    plays, totalBs, totalUsd, rate, banca, taquilla,
    customerName, setCustomerName,
    paymentMethod, setPaymentMethod,
    onBack, onEmit, emitting,
    sessionBlocked, onLogout,
  } = props;
  const methods = [
    { id: "pago_movil", label: "Pago Móvil", desc: "Transferencia inmediata desde tu banco" },
    { id: "debito_inmediato", label: "Débito Inmediato", desc: "Cobro instantáneo con tu tarjeta" },
  ];
  return (
    <div className="min-h-screen bg-background text-foreground p-4 pb-6" data-testid="kiosco-payment-screen">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="kiosco-payment-back"><ArrowLeft size={16} className="mr-1.5"/> Volver</Button>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Paso 2 de 2</div>
            <div className="text-xl font-bold">Finalizar compra</div>
          </div>
        </div>

        {sessionBlocked && (
          <Card className="bg-rose-500/10 border-2 border-rose-500/40 p-4 mb-3" data-testid="kiosco-session-blocked">
            <div className="flex items-start gap-2">
              <ShieldAlert size={22} className="text-rose-300 shrink-0 mt-0.5"/>
              <div className="min-w-0">
                <div className="text-sm font-bold text-rose-100">Límite de compras por sesión alcanzado</div>
                <div className="text-xs font-semibold text-rose-200/80 mt-0.5">Por seguridad, cierra sesión y vuelve a ingresar el PIN para continuar.</div>
              </div>
            </div>
            <Button onClick={onLogout} variant="outline" size="sm" className="w-full mt-3 border-rose-500/40 text-rose-100 hover:bg-rose-500/20" data-testid="kiosco-session-logout">
              Cerrar sesión y volver al PIN
            </Button>
          </Card>
        )}

        <Card className="bg-card border-2 border-primary/40 p-4 mb-3">
          <div className="flex items-baseline justify-between">
            <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Monto a pagar</div>
            <div className="text-3xl font-bold text-primary tabular-nums" data-testid="kiosco-payment-total">Bs {formatBs(totalBs)}</div>
          </div>
          <div className="mt-1 flex items-baseline justify-between text-[11px] font-semibold text-muted-foreground">
            <span>{plays.length} jugada{plays.length !== 1 ? "s" : ""}</span>
            <span className="tabular-nums">≈ $ {formatUsd(totalUsd)} · Tasa Bs {formatBs(rate)}</span>
          </div>
        </Card>

        <Card className="bg-card border-2 border-border p-4 space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Método de pago</Label>
            <div className="grid grid-cols-1 gap-2 mt-1">
              {methods.map((m) => {
                const active = paymentMethod === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPaymentMethod(m.id)}
                    className={`text-left rounded-xl border-2 p-3 transition-colors active:scale-[.98] ${active ? "border-primary bg-primary/10 ring-2 ring-primary/40" : "border-border hover:bg-secondary/60"}`}
                    data-testid={`kiosco-payment-method-${m.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Smartphone size={18} className={active ? "text-primary" : "text-muted-foreground"}/>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold">{m.label}</div>
                        <div className="text-[11px] font-semibold text-muted-foreground">{m.desc}</div>
                      </div>
                      {active && <CheckCircle2 size={18} className="text-primary"/>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Tu nombre (opcional)</Label>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Para tu ticket"
              className="mt-1 h-12"
              data-testid="kiosco-customer-name"
            />
          </div>

          <Button
            onClick={onEmit}
            disabled={emitting || sessionBlocked}
            size="lg"
            className="w-full h-16 text-lg font-bold"
            data-testid="kiosco-emit-ticket"
          >
            {emitting ? "Iniciando pago…" : <>PAGAR AHORA · Bs {formatBs(totalBs)}</>}
          </Button>

          <div className="text-center text-[10px] font-semibold text-muted-foreground pt-1">
            Al continuar aceptas los términos de compra.
          </div>
        </Card>

        <div className="mt-4 text-center text-[11px] font-semibold text-muted-foreground">
          {taquilla?.name} · {banca?.name}
        </div>
      </div>
    </div>
  );
}

function ProcessingScreen({ elapsed, totalBs, onCancel }) {
  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center p-4" data-testid="kiosco-processing-screen">
      <div className="w-full max-w-sm text-center">
        <div className="relative mx-auto h-24 w-24 mb-4">
          <div className="absolute inset-0 rounded-full border-4 border-primary/20"/>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin"/>
          <div className="absolute inset-0 grid place-items-center">
            <Smartphone size={30} className="text-primary"/>
          </div>
        </div>
        <h1 className="text-2xl font-bold">Procesando pago</h1>
        <p className="mt-2 text-sm font-semibold text-muted-foreground">
          Estamos confirmando tu pago. No cierres esta pantalla.
        </p>
        <div className="mt-4 rounded-xl border-2 border-border bg-card p-3">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Monto</div>
          <div className="text-2xl font-bold text-primary tabular-nums">Bs {formatBs(totalBs)}</div>
        </div>
        <div className="mt-4 text-[11px] font-semibold text-muted-foreground tabular-nums" data-testid="kiosco-processing-elapsed">
          Tiempo transcurrido: {elapsed}s
        </div>
        {elapsed >= 30 && (
          <Button variant="outline" size="sm" onClick={onCancel} className="mt-4" data-testid="kiosco-processing-cancel">
            Cancelar y volver
          </Button>
        )}
      </div>
    </div>
  );
}

function TicketScreen({ ticket, banca, taquilla, onRestart, rate, lotteryMap, sorteoMap }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const validationUrl = `${origin}/validar/${ticket.code}`;
  const [btBusy, setBtBusy] = useState(false);
  const btAvailable = typeof window !== "undefined" && isWebBluetoothAvailable();
  const savedPrinter = btAvailable ? getSavedPrinter() : null;
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(ticket.code);
      toast.success("Serial copiado");
    } catch { toast.error("No se pudo copiar"); }
  };
  const shareWhatsapp = () => {
    const url = buildWhatsappTicketUrl(ticket, { validationUrl, operatorName: taquilla?.name || "", lotteryMap, sorteoMap });
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const btPrint = async () => {
    setBtBusy(true);
    const tId = toast.loading(savedPrinter ? `Conectando a ${savedPrinter.name}…` : "Selecciona tu impresora…");
    const res = await printTicketViaBluetooth(ticket, {
      lotteryMap, sorteoMap, isReprint: false, operatorName: taquilla?.name || "", validationUrl,
    });
    setBtBusy(false);
    toast.dismiss(tId);
    if (res.ok) toast.success(`Impreso en ${res.device?.name || "impresora"}`);
    else if (!/cancel|NotFoundError/i.test(res.error || "")) toast.error("Bluetooth: " + res.error);
  };
  return (
    <div className="min-h-screen bg-background text-foreground p-4 pb-6" data-testid="kiosco-ticket-screen">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-3">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500/40 grid place-items-center">
            <CheckCircle2 size={30} className="text-emerald-300"/>
          </div>
          <h1 className="mt-3 text-2xl font-bold text-emerald-200">¡Pago aprobado!</h1>
          <p className="mt-1 text-sm font-semibold text-muted-foreground">Guarda o imprime tu ticket. Válido para el sorteo del día.</p>
        </div>

        <Card className="bg-card border-2 border-emerald-500/40 p-4">
          <div className="text-center">
            <div className="text-[10px] font-bold tracking-widest text-muted-foreground">SERIAL</div>
            <div className="text-2xl font-mono tabular-nums font-bold tracking-[0.15em]" data-testid="kiosco-ticket-code"><CopyCode code={ticket.code} size={18} /></div>
          </div>

          <div className="mt-4 flex justify-center">
            <QRCodeCanvas value={validationUrl} size={180} level="M" includeMargin={true} bgColor="#ffffff" fgColor="#000000"/>
          </div>

          <div className="mt-4 space-y-1.5 border-y-2 border-border py-3 max-h-[180px] overflow-y-auto">
            {ticket.plays.map((p, i) => {
              const lot = lotteryMap[p.lottery_type_id];
              const sor = sorteoMap[p.sorteo_id];
              return (
                <div key={i} className="flex justify-between text-xs font-semibold">
                  <div className="min-w-0">
                    <span className="font-mono font-bold text-sm mr-2">{p.number}</span>
                    {lot?.name}{p.sign ? ` · ${p.sign}` : ""}
                    <span className="text-muted-foreground"> · {sor ? formatTime(sor.scheduled_at) : ""}</span>
                  </div>
                  <div className="font-mono tabular-nums">Bs {formatBs(p.amount_bs)}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex justify-between font-bold text-lg">
            <span>TOTAL</span>
            <span className="tabular-nums text-primary">Bs {formatBs(ticket.total_bs)}</span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground font-semibold">
            <span>Estado del pago</span>
            <span className="font-mono text-emerald-300">APROBADO</span>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button variant="outline" size="lg" onClick={copyCode} className="h-12" data-testid="kiosco-copy-code">
              <Copy size={16} className="mr-2"/> Copiar serial
            </Button>
            <Button size="lg" onClick={onRestart} className="h-12" data-testid="kiosco-new-ticket">
              Nueva compra
            </Button>
          </div>
          <Button
            variant="outline"
            size="lg"
            onClick={shareWhatsapp}
            className="w-full h-12 mt-2 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
            data-testid="kiosco-whatsapp-share"
          >
            <MessageCircle size={16} className="mr-2"/> Compartir por WhatsApp
          </Button>
          {btAvailable && (
            <Button
              variant="outline"
              size="lg"
              onClick={btPrint}
              disabled={btBusy}
              className="w-full h-12 mt-2 border-primary/40 text-primary hover:bg-primary/10"
              data-testid="kiosco-bt-print"
            >
              <Bluetooth size={16} className="mr-2"/>
              {btBusy ? "Imprimiendo…" : (savedPrinter ? "Imprimir en " + savedPrinter.name : "Imprimir por Bluetooth")}
            </Button>
          )}
        </Card>

        <div className="mt-4 text-center text-[11px] font-semibold text-muted-foreground">
          {taquilla?.name} · {banca?.name} · Vuelta al inicio en 60s
        </div>
      </div>
    </div>
  );
}


function CaptchaDialog({ captcha, answer, setAnswer, onSubmit, onClose, loading }) {
  const open = !!captcha;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="bg-card border-2 border-amber-500/40 max-w-sm p-0 overflow-hidden" data-testid="kiosco-captcha-dialog">
        <DialogTitle className="sr-only">Verificación</DialogTitle>
        <DialogDescription className="sr-only">Responde la operación para continuar.</DialogDescription>
        <div className="p-5 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-500/15 border-2 border-amber-500/40 grid place-items-center">
            <ShieldAlert size={26} className="text-amber-300"/>
          </div>
          <h2 className="mt-3 text-xl font-bold">Verifica que no eres un robot</h2>
          <p className="mt-1 text-xs font-semibold text-muted-foreground">Resuelve esta operación sencilla para continuar.</p>
          <div className="mt-4 rounded-xl border-2 border-border bg-secondary/30 py-4">
            <div className="text-3xl font-mono font-bold tabular-nums" data-testid="kiosco-captcha-question">
              {captcha?.question} = ?
            </div>
          </div>
          <Input
            value={answer}
            onChange={(e) => setAnswer(e.target.value.replace(/[^0-9-]/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter") onSubmit?.(); }}
            placeholder="Respuesta"
            inputMode="numeric"
            className="mt-3 h-12 text-center text-xl font-bold tabular-nums"
            data-testid="kiosco-captcha-answer"
            autoFocus
          />
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={onClose} className="h-11" data-testid="kiosco-captcha-cancel">
              Cancelar
            </Button>
            <Button onClick={onSubmit} disabled={loading || !answer.trim()} className="h-11" data-testid="kiosco-captcha-submit">
              {loading ? "Verificando…" : "Continuar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
